/**
 * Component: DatePresetPicker
 * SpecReferenceID: REPORTING-V1
 * Requirements: REPORTING-V1-R12
 *
 * Date-range picker for the REPORTING-V1 admin page. Interprets every
 * selected date as a whole UTC calendar day regardless of the viewer's
 * local timezone — a user in any timezone picking 2026-04-20 means the
 * day 2026-04-20 in UTC terms, not a local-midnight interpretation.
 *
 * Resolved ranges are returned as inclusive-start / exclusive-end ISO
 * timestamps matching the backend's `WHERE created_at >= :start AND
 * created_at < :end` contract. A range covering the single day
 * 2026-04-20 UTC is therefore `{ start: "2026-04-20T00:00:00Z", end:
 * "2026-04-21T00:00:00Z" }`.
 *
 * Presets are bounded at today 00:00Z (end-of-previous-UTC-day) so
 * reports cover only complete days. This is distinct from ADMIN-V1's
 * DateRangePicker which uses local-time semantics.
 *
 * Accessibility: WCAG 2.1 AA compliant with keyboard navigation via
 * native date inputs.
 *
 * Risk Links: R6
 * FMEA Links: FM5
 */

import React from 'react';

import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Preset identifier. `custom` switches on the two manual date inputs.
 * Every other preset resolves deterministically from the current UTC now.
 */
export type PresetId =
  | 'yesterday'
  | 'last_7_days'
  | 'month_to_date'
  | 'last_month'
  | 'last_30_days'
  | 'custom';

/**
 * Resolved range: inclusive-start / exclusive-end ISO 8601 UTC
 * timestamps. `presetId` is echoed so callers can render "Range based
 * on: Month-to-date" text without tracking the preset separately.
 */
export interface ResolvedRange {
  start: string;
  end: string;
  presetId: PresetId;
  /** Set to `true` when month-to-date auto-shifted to last-month on the 1st. */
  autoShifted?: boolean;
}

export interface DatePresetPickerProps {
  value: ResolvedRange;
  onChange: (range: ResolvedRange) => void;
  disabled?: boolean;
  className?: string;
  /**
   * Injectable "now" provider so unit tests can pin the clock without
   * mocking global Date. Defaults to `() => new Date()`.
   */
  nowProvider?: () => Date;
}

const PRESET_OPTIONS: readonly { id: PresetId; label: string }[] = [
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last_7_days', label: 'Last 7 days' },
  { id: 'month_to_date', label: 'Month-to-date' },
  { id: 'last_month', label: 'Last month' },
  { id: 'last_30_days', label: 'Last 30 days' },
  { id: 'custom', label: 'Custom range' },
];

/**
 * Format a Date's UTC components as a YYYY-MM-DD string. Used to feed
 * the `<input type="date">` elements which expect date-only strings.
 */
function toUtcDateString(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parse a YYYY-MM-DD string as the start-of-day UTC ISO timestamp.
 * Throws on invalid input; callers gate on input validity before calling.
 */
function utcDayStartIso(dateStr: string): string {
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some((p) => Number.isNaN(p))) {
    throw new Error(`Invalid YYYY-MM-DD date string: ${dateStr}`);
  }
  const [y, m, d] = parts as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
  return dt.toISOString().replace('.000Z', 'Z');
}

/**
 * Return today's UTC date at 00:00:00 as the exclusive upper bound used
 * by every end-of-yesterday preset. Separate from utcDayStartIso so
 * callers can ask "what does the picker consider 'now'?" without
 * recomputing the ISO string.
 */
function todayUtc00(now: Date): Date {
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0),
  );
}

function addDaysUtc(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setUTCDate(copy.getUTCDate() + days);
  return copy;
}

function firstOfMonthUtc(year: number, month: number): Date {
  return new Date(Date.UTC(year, month, 1));
}

/**
 * Resolve a preset to a start/end range. All presets return UTC
 * 00:00 bounds. `month_to_date` auto-shifts to `last_month` on the
 * 1st of the UTC month (the edge case flagged in the R12 spec).
 */
export function resolvePreset(
  preset: PresetId,
  now: Date,
  customStart?: string,
  customEnd?: string,
): ResolvedRange {
  const today0 = todayUtc00(now);

  if (preset === 'yesterday') {
    const yesterday = addDaysUtc(today0, -1);
    return {
      start: yesterday.toISOString().replace('.000Z', 'Z'),
      end: today0.toISOString().replace('.000Z', 'Z'),
      presetId: 'yesterday',
    };
  }

  if (preset === 'last_7_days') {
    const start = addDaysUtc(today0, -7);
    return {
      start: start.toISOString().replace('.000Z', 'Z'),
      end: today0.toISOString().replace('.000Z', 'Z'),
      presetId: 'last_7_days',
    };
  }

  if (preset === 'last_30_days') {
    const start = addDaysUtc(today0, -30);
    return {
      start: start.toISOString().replace('.000Z', 'Z'),
      end: today0.toISOString().replace('.000Z', 'Z'),
      presetId: 'last_30_days',
    };
  }

  if (preset === 'last_month') {
    const y = today0.getUTCFullYear();
    const m = today0.getUTCMonth();
    const start = firstOfMonthUtc(y, m - 1);
    const end = firstOfMonthUtc(y, m);
    return {
      start: start.toISOString().replace('.000Z', 'Z'),
      end: end.toISOString().replace('.000Z', 'Z'),
      presetId: 'last_month',
    };
  }

  if (preset === 'month_to_date') {
    const firstOfMonth = firstOfMonthUtc(
      today0.getUTCFullYear(),
      today0.getUTCMonth(),
    );
    // Edge case: on the 1st of the UTC month the range is empty
    // (start == end). Auto-shift to last-month so the page always
    // renders a meaningful report range.
    if (firstOfMonth.getTime() === today0.getTime()) {
      const shifted = resolvePreset('last_month', now);
      return { ...shifted, autoShifted: true };
    }
    return {
      start: firstOfMonth.toISOString().replace('.000Z', 'Z'),
      end: today0.toISOString().replace('.000Z', 'Z'),
      presetId: 'month_to_date',
    };
  }

  // Custom: caller-picked dates. The picked end is the last day to
  // INCLUDE, so the exclusive upper bound is picked-end + 1 day.
  const safeStart =
    customStart ?? toUtcDateString(addDaysUtc(today0, -7));
  const safeEnd = customEnd ?? toUtcDateString(addDaysUtc(today0, -1));

  const startIso = utcDayStartIso(safeStart);
  const endDay = new Date(utcDayStartIso(safeEnd));
  const endIso = addDaysUtc(endDay, 1).toISOString().replace('.000Z', 'Z');

  return { start: startIso, end: endIso, presetId: 'custom' };
}

/**
 * Initial range resolver exposed for callers (ReportingPage) that need
 * to seed state before first render.
 */
export function initialRange(nowProvider?: () => Date): ResolvedRange {
  const now = (nowProvider ?? (() => new Date()))();
  return resolvePreset('month_to_date', now);
}

function formatUtcDisplay(iso: string): string {
  return iso.slice(0, 10);
}

export function DatePresetPicker({
  value,
  onChange,
  disabled = false,
  className,
  nowProvider,
}: DatePresetPickerProps): React.ReactElement {
  const now = (nowProvider ?? (() => new Date()))();

  // Custom-range local inputs. Kept in component state so the user can
  // type through a partial date without the parent seeing intermediate
  // invalid states.
  const [customStartInput, setCustomStartInput] = React.useState(() =>
    value.presetId === 'custom' ? formatUtcDisplay(value.start) : '',
  );
  const [customEndInput, setCustomEndInput] = React.useState(() => {
    if (value.presetId !== 'custom') return '';
    // end is exclusive; display as last-included day (end - 1)
    const endDate = new Date(value.end);
    return toUtcDateString(addDaysUtc(endDate, -1));
  });

  const handlePresetChange = (id: PresetId): void => {
    if (id === 'custom') {
      // Seed custom inputs from the current range if not already set.
      if (!customStartInput) {
        setCustomStartInput(formatUtcDisplay(value.start));
      }
      if (!customEndInput) {
        const endDate = new Date(value.end);
        setCustomEndInput(toUtcDateString(addDaysUtc(endDate, -1)));
      }
      // Emit a resolved custom range using current inputs (or defaults).
      onChange(
        resolvePreset(
          'custom',
          now,
          customStartInput || undefined,
          customEndInput || undefined,
        ),
      );
      return;
    }
    onChange(resolvePreset(id, now));
  };

  const handleCustomStartChange = (val: string): void => {
    setCustomStartInput(val);
    if (val && customEndInput) {
      onChange(resolvePreset('custom', now, val, customEndInput));
    }
  };

  const handleCustomEndChange = (val: string): void => {
    setCustomEndInput(val);
    if (customStartInput && val) {
      onChange(resolvePreset('custom', now, customStartInput, val));
    }
  };

  const rangeDisplay = `${formatUtcDisplay(value.start)} to ${formatUtcDisplay(
    new Date(new Date(value.end).getTime() - 1).toISOString(),
  )}`;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Select
          value={value.presetId}
          onValueChange={(id) => handlePresetChange(id as PresetId)}
          disabled={disabled}
        >
          <SelectTrigger
            className="w-full sm:w-48"
            aria-label="Select reporting period"
          >
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            {PRESET_OPTIONS.map((opt) => (
              <SelectItem key={opt.id} value={opt.id}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {value.presetId === 'custom' && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="date"
              aria-label="Custom range start date (UTC)"
              value={customStartInput}
              max={customEndInput || undefined}
              onChange={(e) => handleCustomStartChange(e.target.value)}
              disabled={disabled}
              className="rounded border border-input bg-background px-3 py-2 text-sm"
            />
            <span className="text-sm text-muted-foreground">to</span>
            <input
              type="date"
              aria-label="Custom range end date (UTC, inclusive)"
              value={customEndInput}
              min={customStartInput || undefined}
              onChange={(e) => handleCustomEndChange(e.target.value)}
              disabled={disabled}
              className="rounded border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
        )}
      </div>

      <div className="text-sm text-muted-foreground">
        Range (UTC): <span className="font-mono">{rangeDisplay}</span>
        {value.autoShifted && (
          <span className="ml-2 text-amber-600 dark:text-amber-500">
            (no complete days yet this month — defaulted to Last month)
          </span>
        )}
      </div>
    </div>
  );
}

export default DatePresetPicker;
